// 실전 기록 (the deployment layer) — PURE. The member's single, global log of
// occasions where they used (사용) or encountered (들음) an English
// expression, plus dated intentions (예정) that surface themselves on the
// day. Not tied to a domain, not a progress widget: a keepsake.
//
// The OWNER record (users/{uid}/engineDeployments/{id}) — v2, 2026-08-31.
// Owner-only, full fidelity. Nothing here is public: the public card is a
// separate document, deployFeed/{id}, written ONLY by the projection
// function from an explicit allowlist (functions/deployProjection.js).
// Firestore has no per-field read security, so privacy is structural — the
// private fields below simply never exist in a document a member can read.
//   { title,                    // the occasion (door ①③); '' on door ②  PRIVATE
//     kind: 'used'|'heard',     // the public card calls this `type` (spec §9)
//     scene: string|null,       // the 장면 chip key (SCENES_*)         public
//     scene_other: string,      // free text behind the 기타 chip        PRIVATE
//     note: string,             // the member's one-line note            PRIVATE
//     dueDate: 'YYYY-MM-DD'|null, dueTime: 'HH:MM'|null,   // door ① only PRIVATE
//     expressions: [{uid, en, ko, used}],  // snapshots; `used` persists the
//                                          // mark — false = reserved but
//                                          // never used (queryable, ruled)
//     sentence: '',                                                    public
//     confirmedAt: number|null, // the mark-and-confirm stamp: 건너뛰기 never
//                               // gates, so confirmation completes a record
//                               // even with no sentence (ruled R3)
//     snoozeCount, snoozedKey,  // the 아직요 path: once more tomorrow, then
//                               // quiet conversion to an ordinary pending
//     surfacedKey,              // day-of dedupe for prep_surfaced
//     visibility: 'public'|'private',  // DERIVED, see deriveVisibility
//     createdAt, updatedAt, completedAt: number|null }
//
// STATE IS DERIVED: a record is COMPLETE when it holds a sentence OR a
// confirmation; otherwise it is PENDING (an intention).
// VISIBILITY IS DERIVED: public only when complete AND a sentence exists —
// a 건너뛰기 completion (confirmed, no sentence) stays private forever, since
// the public card requires the sentence (ruled Q9, 2026-08-31).
import { dateKeyOf, addDays } from './engineScheduler';

/** The three lives a record can be in — glyph+label everywhere (ruled). */
export const TYPE_KO = { plan: '예정', used: '사용', heard: '들음' };

/** The 장면 chips (ruled R5 → v2 §2 "required 장면 chips with 기타 free
 *  input"). Keys are what the record stores and the projection publishes;
 *  labels are what the member taps. 기타 opens the free input, whose text
 *  stays PRIVATE (scene_other) — only the key is ever public. */
/*  `short` and `icon` are the TILE's (Eddie 2026-09-16: the pills read as
 *  decoration and he missed that the scene had to be chosen). `ko` is still
 *  the label everything else quotes, so nothing the member reads elsewhere
 *  moved — and scenePhrase keeps its own table regardless. */
export const SCENES_USED = [
  { key: 'meeting', ko: '미팅', short: '미팅', icon: 'mdi-account-group-outline' },
  { key: 'email', ko: '이메일', short: '이메일', icon: 'mdi-email-outline' },
  { key: 'message', ko: '문자', short: '문자', icon: 'mdi-message-text-outline' },
  { key: 'document', ko: '문서 · 리포트', short: '문서', icon: 'mdi-file-document-outline' }, // back as one pill (second walk, 2026-09-04)
  { key: 'other', ko: '기타', short: '기타', icon: 'mdi-dots-horizontal' },
];
/** Keys older records still carry (팀 미팅 and 1:1 미팅 merged into 미팅;
 *  동료 · 상사 removed from 들었어요 on the second walk). They keep rendering
 *  everywhere a key is read; nothing writes them any more. */
export const LEGACY_SCENES = { team_meeting: '미팅', one_on_one: '미팅', colleague: '동료 · 상사' };
/** The heard door's source-shaped set (second walk 2026-09-04: 동료 · 상사
 *  removed; one row at 390). */
export const SCENES_HEARD = [
  { key: 'meeting', ko: '미팅', short: '미팅', icon: 'mdi-account-group-outline' },
  { key: 'media', ko: '영상 · 팟캐스트', short: '영상', icon: 'mdi-play-circle-outline' },
  { key: 'email_doc', ko: '이메일 · 문서', short: '이메일', icon: 'mdi-email-outline' },
  { key: 'other', ko: '기타', short: '기타', icon: 'mdi-dots-horizontal' },
];

/*  ── 추천 표현 (Eddie 2026-09-16, revised the same day) ────────────────────
 *  "just displaying that input bar for 사용할 표현 creates a barrier for
 *  searching since they might not know what to look for" — and then, of the
 *  first build: "i would still like the list dynamically shown and not the
 *  same every single time."
 *
 *  So the empty search box answers for itself, and it answers differently
 *  each time the sheet opens. Three signals, in this order:
 *
 *  1. THE SITUATION THE MEMBER TYPED. 상황 이름 ("수요일 파트너사 미팅",
 *     "싱가포르 리포트") and 기타's own words are run token by token through
 *     the library's existing ranker; whatever they hit leads. This is the only
 *     signal that knows the actual occasion rather than the channel.
 *  2. LEARNED BUT NEVER DEPLOYED. A phrase the member finished and has not yet
 *     used anywhere is the one the loop exists to surface — above a phrase
 *     they already deployed, above one they have never met.
 *  3. THE CHANNEL. Anything rated 어색해요 for this scene is dropped outright;
 *     what remains is grouped by the contexts the scene lives in.
 *
 *  Then the list ROTATES: within each tier — where every candidate is equally
 *  entitled — the starting point moves with the seed the sheet was opened
 *  with, so a member who opens the door twice does not read the same four
 *  phrases twice. Rotation never crosses a tier, so a hit on what they typed
 *  still leads, and a phrase they have never met never displaces one they
 *  learned. Same seed -> same list, which is what keeps the sheet steady while
 *  it is open and keeps the guards honest.
 */
const SCENE_FIT = { meeting: 'fit_meeting', email: 'fit_email', message: 'fit_message', document: 'fit_email', media: 'fit_smalltalk', email_doc: 'fit_email' };
const SCENE_CONTEXTS = {
  meeting: ['미팅 진행', '일정', '관용표현'],
  email: ['팔로업', '일정', '관용표현'],
  message: ['팔로업', '일정', '관용표현'],
  document: ['팔로업', '뉴스 표현', '관용표현'],
  media: ['뉴스 표현', '관용표현'],
  email_doc: ['팔로업', '뉴스 표현', '관용표현'],
};
const FIT_RANK = { 자연스러워요: 0, 무난해요: 1 };

/*  What the member types into 상황 이름 is mostly [when] + [who] + [what] —
 *  "수요일 파트너사 미팅", "싱가포르 팀 메일". Simulated against that shape, a
 *  loose match did real damage: 금요일 and 오늘 appear in dozens of EXAMPLE
 *  sentences, so the noise outranked the member's own learned phrase and a
 *  titled reservation gave worse suggestions than an untitled one ("분기
 *  킥오프" led with `federal`). So the match is strict on both sides — the
 *  calendar and channel words are dropped (the tile already said 미팅), and
 *  only the fields that carry MEANING are searched, never the examples.
 *  What survives is the case worth serving: a member who types something
 *  substantive — 연봉 협상, 가격 인상 안내 — and gets phrases for it. */
const TEXT_STOP = new Set(['오늘', '내일', '어제', '이번', '다음', '지난', '지난주', '이번주', '다음주', '주간', '오전', '오후',
  '월요일', '화요일', '수요일', '목요일', '금요일', '토요일', '일요일', '분기', '상반기', '하반기',
  '준비', '관련', '회의', '미팅', '메일', '이메일', '문자', '문서', '리포트', '보고', '공유']);
const TEXT_FIELDS = ['expression_en', 'meaning_ko', 'when_to_use_ko', 'subScenario', 'context'];
const lower = (v) => String(v || '').toLowerCase();

/** The uids the member's own words genuinely reach. */
export function textHits(corpus, text) {
  const toks = String(text || '').split(/[\s,·]+/)
    .filter((t) => t.length >= 2 && !TEXT_STOP.has(t)).slice(0, 6);
  const out = new Set();
  if (!toks.length) return out;
  for (const x of corpus || []) {
    const hay = TEXT_FIELDS.map((f) => lower(x[f]));
    if (toks.some((t) => hay.some((h) => h.includes(lower(t))))) out.add(String(x.uid));
  }
  return out;
}

/** A stable scramble: the same (uid, seed) always lands in the same place, so
 *  one sheet holds still, and a new seed reshuffles the group. A plain
 *  rotation was not enough — it only moves the wrap point, so the second,
 *  third and fourth lines came out identical open after open. */
function seededOrder(uid, seed) {
  let h = ((uid | 0) * 2654435761 + (seed | 0) * 2246822519) >>> 0;
  h ^= h >>> 13;
  h = (h * 1274126177) >>> 0;
  return h;
}

/** The scene's recommendations.
 *  - `learned`   library recIds finished in the engine
 *  - `learning`  library recIds started and not yet finished — day one's only
 *                personal signal, since nothing has been finished yet
 *  - `deployed`  library uids already used in a completed record
 *  - `picked`    what is already on the draft
 *  - `text`      상황 이름 / 기타's own words
 *  - `seed`      captured once when the sheet opens
 *
 *  The order, and why: what their own words reached (they asked for it), then
 *  what they LEARNED AND HAVE NEVER USED (the gap the loop exists to close),
 *  then what they are learning now, then what they learned and already used,
 *  then the channel's own. 기타 has no channel: it lives on their words, and
 *  failing those on their own unused phrases, which are safe anywhere.
 */
export function recommendExpressions(corpus, {
  scene, learned = [], learning = [], deployed = [], picked = [], text = '', seed = 0,
} = {}, limit = 4) {
  const fit = SCENE_FIT[scene];
  const hits = textHits(corpus, text);
  const know = learned instanceof Set ? learned : new Set(learned);
  const onIt = learning instanceof Set ? learning : new Set(learning);
  const used = deployed instanceof Set ? deployed : new Set(deployed);
  if (!fit && !hits.size && !know.size) return [];
  const has = new Set((picked || []).map((p) => String(p && p.uid !== undefined ? p.uid : p)));
  const ctx = SCENE_CONTEXTS[scene] || [];
  const rows = [];
  for (const x of corpus || []) {
    const uid = String(x.uid);
    if (has.has(uid)) continue;
    const f = fit ? FIT_RANK[x[fit]] : 0;
    if (f === undefined) continue; // 어색해요 for this channel is never offered
    const mine = know.has(x.recordId);
    const fresh = mine && !used.has(uid);
    const hit = hits.has(uid);
    if (!fit && !hit && !fresh) continue; // 기타: their words, else their own unused
    const c = ctx.indexOf(x.context);
    rows.push({
      x,
      tier: hit ? 0 : (mine ? (fresh ? 1 : 3) : (onIt.has(x.recordId) ? 2 : 4)),
      ctx: c === -1 ? ctx.length : c,
      fit: f,
      uid: Number(uid) || 0,
      mine,
      fresh,
      onIt: onIt.has(x.recordId),
    });
  }
  // Grouped by (tier, context) and scrambled INSIDE the group: the channel's
  // own phrases still lead, and it is a different one of them each time.
  const groups = new Map();
  for (const r of rows) {
    const k = `${r.tier}:${r.ctx}`;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  }
  const keys = [...groups.keys()].sort((a, b) => {
    const [ta, ca] = a.split(':').map(Number);
    const [tb, cb] = b.split(':').map(Number);
    return ta - tb || ca - cb;
  });
  const ordered = [];
  for (const k of keys) {
    // fit still leads inside a group; the scramble decides between equals, so
    // a 자연스러워요 phrase is never pushed under a 무난해요 one for variety's sake
    ordered.push(...groups.get(k).sort((a, b) => a.fit - b.fit
      || seededOrder(a.uid, seed) - seededOrder(b.uid, seed)
      || a.uid - b.uid));
  }
  // One per 세부 상황 first: ranked straight down the list, 미팅 returned four
  // ways of saying "I didn't catch that". Four phrases that do four different
  // jobs is what makes the list worth reading.
  const out = [];
  const seen = new Set();
  for (const r of ordered) {
    if (out.length >= limit) break;
    const sub = r.x.subScenario || '';
    if (seen.has(sub)) continue;
    seen.add(sub);
    out.push(r);
  }
  for (const r of ordered) {
    if (out.length >= limit) break;
    if (!out.includes(r)) out.push(r);
  }
  return out.map((r) => ({ ...r.x, _learned: r.mine, _fresh: r.fresh, _onIt: r.onIt }));
}

/** One phrase per scene with its own particle (ruled 2026-09-02): the
 *  card's meta row and the bar's name lines. 기타 and unknown → 실전. */
export function scenePhrase(type, scene, sceneOther = '') {
  const used = type !== 'heard';
  const table = used
    ? { meeting: '미팅에서 썼어요', team_meeting: '미팅에서 썼어요', one_on_one: '미팅에서 썼어요', email: '이메일에서 썼어요', message: '문자로 썼어요', document: '문서 · 리포트에 썼어요' }
    : { meeting: '미팅에서 들었어요', colleague: '동료 · 상사에게 들었어요', media: '영상 · 팟캐스트에서 들었어요', email_doc: '이메일 · 문서에서 봤어요' }; // written sources are 봤어요 (ruled 2026-09-05)
  if (table[scene]) return table[scene];
  // 기타 is PUBLIC (second walk 2026-09-04): the member's own words are the
  // scene. Joined with the interpunct, never a particle — 컨퍼런스에서 works,
  // 출장 중에서 does not, and no template can know which it got.
  const own = scene === 'other' ? String(sceneOther || '').trim() : '';
  if (own) return `${own} · ${used ? '썼어요' : '들었어요'}`;
  return used ? '실전에서 썼어요' : '실전에서 들었어요';
}

/** The feed's shape (ruled 2026-09-02/03): 실전 기록 = records + legacy 인증
 *  folded read-only (labeled), no 질문 posts; 질문방 = its category; 전체 =
 *  everything, records interleaved by time (Q11 settled). */
export const FEED_FILTERS = ['사용했어요', '들었어요', '질문', '자기소개']; // round 6 (2026-09-05): all type filters, with icons; no catch-all (예정 is never public)
/** what each pill is, and what its empty state invites (never absence) */
export const FILTER_META = {
  '사용했어요': { kind: 'used', door: 'used', title: '실전에서 쓴 영어 한 문장, 남겨볼까요?', body: '표현 하나와 그 문장이면 돼요', cta: '사용했어요 남기기' },
  '들었어요': { kind: 'heard', door: 'heard', title: '들은 표현을 남겨볼까요?', body: '영상, 팟캐스트, 이메일에서 마주친 표현 한 줄이면 돼요', cta: '들었어요 남기기' },
  '질문': { kind: 'question', door: 'stuck', title: '궁금한 표현이 있나요?', body: '한 줄로 물어보면 에디와 멤버들이 답해요', cta: '질문 올리기' },
  '자기소개': { kind: 'intro', door: 'intro', title: '처음 오셨다면, 한 줄로 인사해요', body: '어디서 어떤 일을 하는지 한 줄이면 충분해요', cta: '자기소개하기' },
};
export function isLegacyCertified(t) {
  return !!(t && t.category === '인증하기' && String(t.expression || '').trim());
}
export function feedFor(filter, cards, threads) {
  const recs = (cards || []).map((c) => ({ kind: 'record', at: Number(c.completedAt) || 0, id: `r:${c.id}`, card: c }));
  const ths = (threads || []).map((t) => ({ kind: isLegacyCertified(t) ? 'legacy' : 'thread', at: new Date(t.createdAt || 0).getTime() || 0, id: `t:${t.id}`, thread: t }));
  let items;
  if (filter === '사용했어요') items = [...recs.filter((x) => x.card.type === 'used'), ...ths.filter((x) => x.kind === 'legacy')]; // legacy 인증 folds here
  else if (filter === '들었어요') items = recs.filter((x) => x.card.type === 'heard');
  else if (filter === '질문') items = ths.filter((x) => x.thread.category === '질문방');
  else if (filter === '자기소개') items = ths.filter((x) => x.thread.category === '자기소개');
  else items = [...recs, ...ths];
  return items.sort((a, b) => b.at - a.at);
}

/** Local mode has no projection function: this is the card the function
 *  would write, built from the owner record on this device (deployFeedService
 *  local twin). Same manual-phase rule as functions/deployProjection.js: a
 *  썼어요 starts pending (검토 중), a 들었어요 none. */
export function localCard(r, viewer = {}) {
  const list = (r && r.expressions) || [];
  const first = list.find((e) => e.used !== false) || list[0] || null;
  const card = {
    id: r.id,
    uid: viewer.uid || 'me',
    name: viewer.name || '나',
    role: viewer.role || '',
    avatar: viewer.avatar || '',
    type: r.kind,
    scene: r.scene || null,
    scene_other: r.scene === 'other' ? String(r.scene_other || '').trim() : '', // public since the second walk
    expression: first ? { uid: String(first.uid || ''), en: first.en || '', ko: first.ko || '' } : null,
    expressions: list.filter((e) => e.used !== false).map((e) => ({ uid: String(e.uid || ''), en: e.en || '', ko: e.ko || '' })), // every used one (ruled 2026-09-06)
    expressionUids: list.filter((e) => e.used !== false).map((e) => String(e.uid || '')),
    sentence: String(r.sentence || '').trim(),
    createdAt: r.createdAt || 0,
    completedAt: r.completedAt || 0,
    projectedAt: r.completedAt || 0,
    ai_review: r.ai_review || { status: r.kind === 'used' ? 'pending' : 'none' },
  };
  // 질문 및 메모 is public on a 들었어요 only (ruled 2026-09-04); a 썼어요 has
  // no memo, and an older one's note never leaves the record
  if (r.kind === 'heard') card.note = String(r.note || '').trim();
  return card;
}

export function sceneLabel(key) {
  const all = [...SCENES_USED, ...SCENES_HEARD];
  const hit = all.find((s) => s.key === key);
  return hit ? hit.ko : (LEGACY_SCENES[key] || '');
}

/** Public iff the record is complete AND carries a sentence. Pending
 *  intentions and skipped-sentence confirmations are private. */
export function deriveVisibility(rec) {
  if (!rec) return 'private';
  const hasSentence = !!String(rec.sentence || '').trim();
  return recordState(rec) === 'complete' && hasSentence ? 'public' : 'private';
}

export function recordState(rec) {
  if (!rec) return 'pending';
  return (String(rec.sentence || '').trim() || rec.confirmedAt) ? 'complete' : 'pending';
}

/** Which type mark a record wears: pending → 예정, complete → 사용/들음. */
export function typeOf(rec) {
  if (recordState(rec) === 'pending') return 'plan';
  return rec.kind === 'heard' ? 'heard' : 'used';
}

/** The doors ask for different things — nothing optional (ruled R3), and
 *  since v2 every record door needs its 장면 chip (ruled 2026-09-02); 기타
 *  needs its one private line. The fourth door (질문있어요, ruled 2026-09-04)
 *  is not a record: a free-form question — its title is required, the
 *  situation body is not, so a one-line question stays a question. */
export const DOOR_KINDS = ['plan', 'used', 'heard', 'stuck'];
export function sceneOk(d) {
  const scene = String((d && d.scene) || '').trim();
  if (!scene) return false;
  return scene !== 'other' || !!String(d.scene_other || '').trim();
}
export function canSave(door, d) {
  const hasExpr = Array.isArray(d.expressions) && d.expressions.length >= 1;
  const title = String(d.title || '').trim();
  const sentence = String(d.sentence || '').trim();
  if (door === 'stuck') return !!title;
  if (door === 'plan') return !!(title && (d.dueDate || d.dueWeek) && hasExpr && sceneOk(d));
  if (door === 'heard') return !!(hasExpr && sentence && sceneOk(d));
  // 'used' — one record = one sentence (ruled 2026-09-06): with more than one
  // expression the door asks one sentence per expression (at least one
  // written; an empty field is skipped, its expression kept as used), or, with
  // 한 문장에 같이 썼어요, the one sentence that carries every expression
  const shape = usedShape(d);
  if (shape === 'split') return !!(hasExpr && sceneOk(d) && usedExprs(d).some((e) => String((d.sentences || {})[exprKey(e)] || '').trim()));
  if (shape === 'together') return !!(hasExpr && sceneOk(d) && sentence && carriesAll(sentence, usedExprs(d)));
  return !!(hasExpr && oneSentence(d) && sceneOk(d));
}

/** The one-expression sentence: keyed to that expression when the door keeps
 *  per-expression sentences (so removing another pill never touches it —
 *  phone walk 2026-09-07), else the plain sentence field. */
export function oneSentence(d) {
  const one = usedExprs(d)[0];
  // a plain read, never hasOwnProperty: a reactive draft tracks the read of a
  // key that does not exist yet, so the gate re-runs on the first keystroke
  const keyed = one && d.sentences ? d.sentences[exprKey(one)] : undefined;
  return String((keyed !== undefined ? keyed : d.sentence) || '').trim();
}

/** The used door's shape: one sentence (one expression), split (one field
 *  per expression), or together (one sentence, every expression in it). */
export function usedShape(d) {
  if (usedExprs(d).length < 2) return 'one';
  return d.together ? 'together' : 'split';
}
export const exprKey = (e) => String((e && (e.uid || e.en)) || '');
export const usedExprs = (d) => ((d && d.expressions) || []).filter((e) => e && e.used !== false);

/** The matcher's contract, the SAME as the desk's (reviewBuild.expressionPreserved,
 *  unit-locked to it): every core word, minus ~, present in order, gaps
 *  allowed. The composer checks 한 문장에 같이 썼어요 live with it. */
const cnorm = (w) => String(w || '').toLowerCase().replace(/[’']/g, "'").replace(/^[^\w']+|[^\w']+$/g, '');
const ctok = (t) => String(t || '').trim().split(/\s+/).filter(Boolean);
export function carries(core, sentence) {
  const cw = ctok(core).map(cnorm).filter((w) => w && w !== '~');
  if (!cw.length) return true;
  const sw = ctok(sentence).map(cnorm);
  let k = 0;
  for (const w of sw) { if (w === cw[k]) k += 1; if (k === cw.length) return true; }
  return false;
}
export function carriesAll(sentence, exprs) {
  return (exprs || []).every((e) => carries(typeof e === 'string' ? e : (e && e.en) || '', sentence));
}
/** Which of the expressions the sentence does not carry yet (the gate line). */
export function missingIn(sentence, exprs) {
  return (exprs || []).filter((e) => !carries(typeof e === 'string' ? e : (e && e.en) || '', sentence));
}

/** One record = one sentence, because one sentence gets one review (ruled
 *  2026-09-06). `fields` is one entry per USED expression, in order:
 *  { expr, sentence }; `unused` are the reserved-but-never-used ones. Returns
 *  the records to write: the FIRST replaces the record in place (it keeps
 *  the unused expressions as used:false, the ruled instrument), the rest are
 *  new, one expression each. A field left empty is skipped with its
 *  expression kept as used: its record completes without a sentence (the
 *  mark-and-confirm rule; private forever, like 건너뛰기). With `together`
 *  the one sentence carries every used expression: one record, both
 *  highlighted. Two posts from one occasion are two posts — no tag (ruled). */
export function splitRecords(base, { fields = [], unused = [], together = false, sentence = '', nowMs = Date.now() } = {}) {
  const usedAll = fields.map((f) => ({ ...f.expr, used: true }));
  const rest = unused.map((e) => ({ ...e, used: false }));
  const one = (exprs, sent) => {
    const text = String(sent || '').trim();
    return { ...base, expressions: exprs, sentence: text, confirmedAt: base.confirmedAt || (text ? null : nowMs) };
  };
  if (together || fields.length <= 1) return [one([...usedAll, ...rest], together ? sentence : (fields[0] ? fields[0].sentence : ''))];
  const filled = fields.filter((f) => String(f.sentence || '').trim());
  if (!filled.length) return [one([...usedAll, ...rest], '')]; // every field empty: as 건너뛰기 today
  const first = one([{ ...filled[0].expr, used: true }, ...rest], filled[0].sentence);
  const more = [...filled.slice(1), ...fields.filter((f) => !String(f.sentence || '').trim())]
    .map((f) => ({ ...one([{ ...f.expr, used: true }], f.sentence), snoozeCount: 0, snoozedKey: null, surfacedKey: null }));
  return [first, ...more];
}

/** The beat fires once for a whole occasion (ruled 2026-09-06): every used
 *  pair across the records it wrote, each once. */
export function beatPairsAll(records) {
  const seen = new Set(); const out = [];
  (records || []).forEach((r) => beatPairs(r).forEach((p) => { const k = exprKey(p); if (seen.has(k)) return; seen.add(k); out.push(p); }));
  return out;
}

/** The beat after a reservation (one verb, 예약 — phone walk 2026-09-07) — what was
 *  packed, and honestly when it comes back (the day-of window: T−2h with a
 *  time, all day without, Monday for a week). */
export function reserveBeat(rec, nowMs) {
  const title = '표현을 예약했어요';
  if (!rec) return { title, sub: '' };
  if (!rec.dueDate && rec.dueWeek) return { title, sub: `${whenLabel(rec, nowMs)} 월요일에 다시 보여드릴게요` };
  if (dayOfPhase({ ...rec, sentence: '', confirmedAt: null }, nowMs)) return { title, sub: '데일리 루틴에서 바로 보여드릴게요' };
  if (rec.dueTime) return { title, sub: '당일 2시간 전에 다시 보여드릴게요' };
  return { title, sub: '당일에 다시 보여드릴게요' };
}

/** The 질문있어요 door writes a 질문방 thread, not a record (ruled: one
 *  entrance, one question surface; free-form since 2026-09-04, like a real
 *  question board). The title is the question, the body the situation. */
export function questionThread({ title, body } = {}) {
  return {
    title: String(title || '').trim(),
    message: String(body || '').trim(),
    origin: 'door-question',
  };
}

/** The sentence field's placeholder once an expression is attached (ruled
 *  2026-09-05): that expression's first example sentence from the library,
 *  prefixed 예: so it reads as an example, never a template. A placeholder
 *  is never a value — the field stays empty and the gate stays shut. */
export function examplePlaceholder(expressions, library, fallback) {
  const first = (expressions || []).find((e) => e && e.used !== false) || (expressions || [])[0];
  if (!first) return fallback;
  const item = (library || []).find((x) => String(x.uid) === String(first.uid));
  const ex = item && String(item.example_en || '').trim();
  return ex ? `예: ${ex}` : fallback;
}

/** Library item → the snapshot a record keeps (keepsakes keep their text). */
export function toSnapshot(item) {
  return { uid: String(item.uid), en: item.expression_en, ko: item.meaning_ko, used: true };
}

/** The beat's headline (ruled copy for 사용; the honest parallel for 들음). */
export function beatTitle(kind) {
  return kind === 'heard' ? '실전에서 들었어요' : '실전에서 사용했어요';
}

/** The beat shows only the pairs that were actually used — nothing else. */
export function beatPairs(rec) {
  return ((rec && rec.expressions) || [])
    .filter((e) => e.used !== false)
    .map((e) => ({ en: e.en, ko: e.ko }));
}

// ── search (unchanged from the audited build) ──────────────────────────────
const norm = (s) => String(s || '').toLowerCase();

const PARTICLES = [
  '에서는', '으로는', '에게는', '이라도', '으로도',
  '에서', '에게', '으로', '부터', '까지', '보다', '처럼', '한테', '에는', '에도', '이나', '라도', '이라', '만큼', '조차',
  '을', '를', '이', '가', '은', '는', '에', '의', '도', '만', '과', '와', '로', '랑', '나', '야',
];
const HANGUL = /[가-힣]/;

function stemOf(tok) {
  if (!HANGUL.test(tok)) return null;
  for (const p of PARTICLES) {
    if (tok.length > p.length && tok.endsWith(p)) {
      const stem = tok.slice(0, -p.length);
      if (stem.length >= 2) return stem;
    }
  }
  return null;
}

function tokenScore(item, tok) {
  const en = norm(item.expression_en);
  const ko = norm(item.meaning_ko);
  let best = 0;
  if (en.startsWith(tok)) best = 100;
  else if (en.includes(` ${tok}`)) best = 80;
  else if (en.includes(tok)) best = 50;
  const koIdx = ko.indexOf(tok);
  if (koIdx === 0) best = Math.max(best, 90);
  else if (koIdx > 0) best = Math.max(best, 60);
  const context = [item.when_to_use_ko, item.subScenario, item.quiz_scenario_ko, item.context];
  const examples = [item.example_en, item.example_ko, item.example_en_2, item.example_ko_2];
  const hits = (fields) => fields.filter((f) => f && norm(f).includes(tok)).length;
  const ctxHits = hits(context);
  const exHits = hits(examples);
  if (best === 0 && (ctxHits || exHits)) best = ctxHits ? 30 : 22;
  return best ? best + Math.min(9, (ctxHits + exHits) * 3) : 0;
}

export function searchExpressions(corpus, query, limit = 12) {
  const toks = norm(query).split(/\s+/).filter(Boolean);
  if (!toks.length) return [];
  const stems = toks.map(stemOf);
  const scored = [];
  for (const item of corpus || []) {
    let sum = 0;
    let ok = true;
    for (let i = 0; i < toks.length; i += 1) {
      let s = tokenScore(item, toks[i]);
      if (!s && stems[i]) s = tokenScore(item, stems[i]) * 0.8;
      if (!s) { ok = false; break; }
      sum += s;
    }
    if (ok) scored.push({ item, sum });
  }
  scored.sort((a, b) => b.sum - a.sum
    || String(a.item.expression_en).length - String(b.item.expression_en).length);
  return scored.slice(0, limit).map((s) => s.item);
}

// ── the day-of window (ruled): T−2h → T+3h with a time; all day without.
//    The question asks on the day (after the time), once more the next day,
//    and gives up quietly after that — 아직요 or silence both convert. ─────
const H2 = 2 * 3600e3;
const H3 = 3 * 3600e3;

/** The Monday (local) of the week holding dateKey. */
export function mondayOf(dateKey) {
  const d = new Date(`${dateKey}T12:00:00`);
  return addDays(dateKey, -((d.getDay() + 6) % 7));
}
/** 다음 주 as a reservation: the Monday of next week. */
export function nextWeekKey(nowMs) {
  return addDays(mondayOf(dateKeyOf(nowMs)), 7);
}

/** A week-scoped reservation (ruled 2026-09-04, Catalyst Row §2: "Monday
 *  shows, Friday asks"). Monday: the day-of card all day, informational.
 *  Tue–Thu: nothing but the log. Friday: the question, once. 아직요 moves the
 *  week forward once (the home handler bumps dueWeek); the second 아직요, or
 *  silence past Friday, leaves an ordinary pending. */
function weekPhase(rec, nowMs) {
  if ((rec.snoozeCount || 0) >= 2) return null;
  const todayKey = dateKeyOf(nowMs);
  const mon = rec.dueWeek;
  const fri = addDays(mon, 4);
  if (todayKey < mon) return null;
  if (todayKey === mon) return { phase: 'upcoming' };
  if (todayKey === fri) return rec.snoozedKey === todayKey ? null : { phase: 'question' };
  return null;
}

export function dayOfPhase(rec, nowMs) {
  if (!rec || recordState(rec) === 'complete') return null;
  if (!rec.dueDate && rec.dueWeek) return weekPhase(rec, nowMs);
  if (!rec.dueDate) return null;
  if ((rec.snoozeCount || 0) >= 2) return null; // converted: an ordinary pending
  const todayKey = dateKeyOf(nowMs);
  if (todayKey > addDays(rec.dueDate, 2)) return null; // asked enough; quiet
  const snoozedToday = rec.snoozedKey === todayKey;
  // Timed records anchor on the due INSTANT, not the due date, so the ruled
  // T−2h window crosses midnight: a 00:20 meeting surfaces from 22:20 the
  // night before. The old date-gate (todayKey < dueDate → null) hid exactly
  // that record — found when the e2e seeds started wrapping at 23:50.
  if (rec.dueTime && todayKey <= rec.dueDate) {
    const [h, m] = String(rec.dueTime).split(':').map(Number);
    const due = new Date(`${rec.dueDate}T00:00:00`);
    due.setHours(h, m, 0, 0);
    const t = due.getTime();
    if (nowMs < t - H2) return null;
    if (nowMs < t) return { phase: 'upcoming' };
    if (nowMs <= t + H3) return snoozedToday ? null : { phase: 'question' };
    return null; // window closed — tomorrow asks
  }
  if (todayKey < rec.dueDate) return null; // all-day, still ahead
  if (todayKey > rec.dueDate) return snoozedToday ? null : { phase: 'question' };
  return { phase: 'upcoming' }; // all-day: rehearse today, question tomorrow
}

/** One-line when label for the event card: 오후 3시 · 20분 뒤 family. */
export function whenLabel(rec, nowMs) {
  if (!rec.dueDate && rec.dueWeek) {
    const thisMon = mondayOf(dateKeyOf(nowMs));
    if (rec.dueWeek === thisMon) return '이번 주';
    if (rec.dueWeek === addDays(thisMon, 7)) return '다음 주';
    const d = new Date(`${rec.dueWeek}T00:00:00`);
    return `${d.getMonth() + 1}/${d.getDate()} 주`;
  }
  if (!rec.dueDate) return '';
  const todayKey = dateKeyOf(nowMs);
  let clock = '';
  let rel = '';
  if (rec.dueTime) {
    const [h, m] = String(rec.dueTime).split(':').map(Number);
    const h12 = h % 12 || 12;
    clock = `${h < 12 ? '오전' : '오후'} ${h12}시${m ? ` ${m}분` : ''}`;
    if (todayKey === rec.dueDate) {
      const due = new Date(nowMs);
      due.setHours(h, m, 0, 0);
      const mins = Math.round((due.getTime() - nowMs) / 60000);
      if (mins > 0 && mins <= 120) rel = mins >= 60 ? `${Math.floor(mins / 60)}시간 뒤` : `${mins}분 뒤`;
    } else {
      // a timed plan on ANOTHER day keeps its date — 오후 3시 alone would be
      // indistinguishable from a plan for today
      const d = new Date(`${rec.dueDate}T00:00:00`);
      clock = `${d.getMonth() + 1}/${d.getDate()} ${clock}`;
    }
  } else if (todayKey === rec.dueDate) clock = '오늘';
  else {
    const d = new Date(`${rec.dueDate}T00:00:00`);
    clock = `${d.getMonth() + 1}/${d.getDate()}`;
  }
  return rel ? `${clock} · ${rel}` : clock;
}

/** A reservation the member closed without a record (사용하지 못했어요), or one
 *  whose day has simply passed. Ruled 2026-09-10: the moment is over, so it
 *  never asks again — it drops to the bottom of 사용 예정 with 다시 예약하기. */
export function isClosed(rec, nowMs) {
  if (!rec || recordState(rec) === 'complete') return false;
  if (rec.closedAt) return true;
  const todayKey = dateKeyOf(nowMs);
  if (rec.dueDate) return todayKey > addDays(rec.dueDate, 1);
  if (rec.dueWeek) return todayKey > addDays(rec.dueWeek, 6);
  return false;
}

/** The moment has arrived and the card LIGHTS — for its own day and no longer
 *  (refined 2026-09-10): a timed reservation from its hour to the end of that
 *  day, an all-day one all of it, a week-scoped one on the two days its own
 *  window opens (Monday) and asks (Friday). */
/** D-DAY: the reservation's day is TODAY — all of it, before its hour and
 *  after (ruled 2026-09-13). This is what wears the orange theme: the one
 *  reservation the member can act on today. isLiveNow is narrower (it starts
 *  at the hour) and still drives the "the moment is happening" affordances. */
export function isDueToday(rec, nowMs) {
  if (!rec || isClosed(rec, nowMs) || recordState(rec) === 'complete') return false;
  const todayKey = dateKeyOf(nowMs);
  if (!rec.dueDate && rec.dueWeek) return todayKey === rec.dueWeek || todayKey === addDays(rec.dueWeek, 4);
  return rec.dueDate === todayKey;
}

export function isLiveNow(rec, nowMs) {
  if (!rec || isClosed(rec, nowMs) || recordState(rec) === 'complete') return false;
  const todayKey = dateKeyOf(nowMs);
  if (!rec.dueDate && rec.dueWeek) return todayKey === rec.dueWeek || todayKey === addDays(rec.dueWeek, 4);
  if (rec.dueDate !== todayKey) return false;
  if (!rec.dueTime) return true;
  return nowMs >= dueInstant(rec);
}

/** The instant a timed reservation is due, local. */
function dueInstant(rec) {
  const [h, m] = String(rec.dueTime).split(':').map(Number);
  const d = new Date(`${rec.dueDate}T00:00:00`);
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

/** The moment is OVER, so the card stops offering the phrases and asks
 *  instead — 표현 보기 becomes 사용하셨나요? (ruled 2026-09-10). Three hours
 *  after a timed reservation (the ruled window's own edge), the day after an
 *  all-day one, Friday for a week-scoped one. */
export function resvAsks(rec, nowMs) {
  if (!rec || recordState(rec) === 'complete') return false;
  const todayKey = dateKeyOf(nowMs);
  if (!rec.dueDate && rec.dueWeek) return todayKey >= addDays(rec.dueWeek, 4);
  if (!rec.dueDate) return false;
  if (todayKey > rec.dueDate) return true;
  if (todayKey < rec.dueDate) return false;
  return !!rec.dueTime && nowMs > dueInstant(rec) + H3;
}

/** Its day is behind us: it waits behind everything still ahead (2026-09-10). */
export function isPastDue(rec, nowMs) {
  if (!rec) return false;
  const todayKey = dateKeyOf(nowMs);
  if (rec.dueDate) return todayKey > rec.dueDate;
  if (rec.dueWeek) return todayKey > addDays(rec.dueWeek, 4);
  return false;
}

/** The reservation card's own when line — short enough to sit on one row with
 *  the occasion (ruled 2026-09-10): the day is 오늘 or its date, the hour is
 *  a clock, never 오전 아홉 시 삼십 분 spelled out. */
export function cardClock(dueTime) {
  const [h, m] = String(dueTime).split(':').map(Number);
  const h12 = h % 12 || 12;
  return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'AM' : 'PM'}`;
}

/** The day alone, as a member counts it (ruled 2026-09-11): 오늘, then the
 *  countdown for the week ahead, then 다음 주 when it really is next week, and
 *  a date only when it is further out than that. */
export function cardDay(rec, nowMs) {
  if (!rec) return '';
  if (!rec.dueDate) return whenLabel(rec, nowMs) || '날짜 미정';
  const todayKey = dateKeyOf(nowMs);
  const days = Math.round((Date.parse(`${rec.dueDate}T00:00:00`) - Date.parse(`${todayKey}T00:00:00`)) / 86400000);
  if (days === 0) return '오늘';
  // its day has passed but it is still asking (the one grace day): name the
  // day, never a bare date (ruled 2026-09-13)
  if (days === -1) return '어제';
  if (days >= 1 && days <= 6) return `D-${days}`;
  const nextMon = addDays(mondayOf(todayKey), 7);
  if (days > 0 && rec.dueDate >= nextMon && rec.dueDate <= addDays(nextMon, 6)) return '다음 주';
  return `${Number(rec.dueDate.slice(5, 7))}/${Number(rec.dueDate.slice(8, 10))}`;
}

/** The clock rides with the day whenever the day is a day. A week is not:
 *  다음 주 carries no hour (ruled 2026-09-11). */
export function cardWhen(rec, nowMs) {
  if (!rec) return '';
  const day = cardDay(rec, nowMs);
  const clocked = !!(rec.dueDate && rec.dueTime) && day !== '다음 주';
  return clocked ? `${day} ${cardClock(rec.dueTime)}` : day;
}

// ── home + log derivations ─────────────────────────────────────────────────
/** Live reservations only: a closed one has said its piece (2026-09-10). */
export function pendingCards(records, nowMs = Date.now()) {
  return (records || [])
    .filter((r) => recordState(r) === 'pending' && !isClosed(r, nowMs))
    .sort((a, b) => {
      // FLOW B (ruled 2026-09-13, supersedes "past waits behind whatever is
      // ahead"): today leads, then the one whose day has passed and is still
      // ASKING 사용하셨나요? — answering it closes the loop, so it must stay
      // catchable — and only then what is merely upcoming.
      const rank = (r) => (isDueToday(r, nowMs) ? 0 : isPastDue(r, nowMs) ? 1 : 2);
      const ra = rank(a); const rb = rank(b);
      if (ra !== rb) return ra - rb;
      // among the past ones the most recent asks first (it is the likeliest
      // to be remembered); everywhere else, soonest first
      if (ra === 1) {
        const da = a.dueDate || a.dueWeek || '';
        const db = b.dueDate || b.dueWeek || '';
        if (da !== db) return da < db ? 1 : -1;
      }
      // dated intentions first, soonest first (a week counts from its
      // Monday); undated after, freshest first
      const ka = a.dueDate || a.dueWeek || null;
      const kb = b.dueDate || b.dueWeek || null;
      if (ka && kb) return ka < kb ? -1 : ka > kb ? 1 : 0;
      if (ka) return -1;
      if (kb) return 1;
      return (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0);
    });
}

/** When a reservation ended: its own stamp, else the day it was due. */
function endedAt(rec) {
  if (rec.closedAt) return rec.closedAt;
  if (rec.dueDate) return Date.parse(`${rec.dueDate}T00:00:00`) || 0;
  if (rec.dueWeek) return Date.parse(`${rec.dueWeek}T00:00:00`) || 0;
  return rec.updatedAt || rec.createdAt || 0;
}

/** The reservations that ended without a record, newest first — the tail of
 *  사용 예정 in the log, each offering 다시 예약하기. */
export function closedCards(records, nowMs = Date.now()) {
  return (records || [])
    .filter((r) => recordState(r) === 'pending' && isClosed(r, nowMs))
    .sort((a, b) => endedAt(b) - endedAt(a));
}

export function logGroups(records, nowMs) {
  const done = (records || [])
    .filter((r) => r.legacy || recordState(r) === 'complete')
    .sort((a, b) => (b.completedAt || b.createdAt || 0) - (a.completedAt || a.createdAt || 0));
  const thisYear = new Date(nowMs).getFullYear();
  const groups = [];
  for (const r of done) {
    const d = new Date(r.completedAt || r.createdAt || nowMs);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) {
      const label = d.getFullYear() === thisYear
        ? `${d.getMonth() + 1}월`
        : `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
      g = { key, label, rows: [] };
      groups.push(g);
    }
    g.rows.push(r);
  }
  return groups;
}

/** The review state a record carries (the callable mirrors ai_review onto
 *  the owner record): confirmed · refined · pending · none. */
export function reviewState(rec) {
  const rv = (rec && rec.ai_review) || null;
  if (!rv || rv.status === 'pending') return rv ? 'pending' : 'none';
  if (rv.status === 'done') {
    if (rv.verdict === 'confirmed') return 'confirmed';
    if (rv.verdict === 'refined' && String(rv.refined_sentence || '').trim()) return 'refined';
  }
  return 'none';
}

/** 09 · shelf + stream (ruled 2026-09-05): the stream is everything the
 *  member made, newest first, one flat run — no chapters. */
export function logStream(records) {
  return (records || [])
    .filter((r) => r.legacy || recordState(r) === 'complete')
    .sort((a, b) => (b.completedAt || b.createdAt || 0) - (a.completedAt || a.createdAt || 0));
}

/** The shelf: the expressions the member has actually USED, most recently
 *  used first (no counts, ruled 2026-09-06), each with its best sentence —
 *  the one that came back 이대로 좋아요, else the refined one, else the
 *  latest. */
const BEST_RANK = { confirmed: 3, refined: 2, pending: 1, none: 1 };
export function reuseShelf(records) {
  const rows = new Map();
  logStream(records).filter((r) => (r.legacy ? 'used' : typeOf(r)) === 'used').forEach((r) => {
    const at = r.completedAt || r.createdAt || 0;
    const state = r.legacy ? 'none' : reviewState(r);
    const sentence = String(state === 'refined' ? r.ai_review.refined_sentence : r.sentence || '').trim();
    (r.expressions || []).filter((e) => e && e.used !== false && (e.uid || e.en)).forEach((e) => {
      const key = String(e.uid || e.en);
      let row = rows.get(key);
      if (!row) { row = { key, uid: e.uid || null, en: e.en, ko: e.ko || '', lastAt: 0, best: null }; rows.set(key, row); }
      if (at > row.lastAt) row.lastAt = at;
      if (!row.ko && e.ko) row.ko = e.ko;
      const cand = { id: r.id, sentence, state, at, rank: BEST_RANK[state] };
      if (sentence && (!row.best || cand.rank > row.best.rank || (cand.rank === row.best.rank && cand.at > row.best.at))) row.best = cand;
    });
  });
  return [...rows.values()].sort((a, b) => b.lastAt - a.lastAt);
}

/** The stream narrowed by the toggle and by a shelf chip. */
export function streamFilter(rows, { seg = 'all', key = null } = {}) {
  return (rows || []).filter((r) => (seg === 'all' || (r.legacy ? 'used' : typeOf(r)) === seg)
    && (!key || (r.expressions || []).some((e) => e && e.used !== false && String(e.uid || e.en) === key)));
}

/** The cumulative header (ruled: count RECORDS, not expressions):
 *  기록 40개 · 5월부터 — 부터 appears once the log spans months. */
export function logHeader(groups, nowMs) {
  const count = groups.reduce((n, g) => n + g.rows.length, 0);
  if (!count) return '';
  const last = groups[groups.length - 1];
  const nowKey = `${new Date(nowMs).getFullYear()}-${String(new Date(nowMs).getMonth() + 1).padStart(2, '0')}`;
  if (groups.length > 1 || last.key !== nowKey) {
    const [y, m] = last.key.split('-').map(Number);
    const from = y === new Date(nowMs).getFullYear() ? `${m}월부터` : `${y}년 ${m}월부터`;
    return `기록 ${count}개 · ${from}`;
  }
  return `기록 ${count}개`;
}

// ── save stamps (audited build, state definition updated) ──────────────────
export function withDerivedStamps(next, prev, nowMs) {
  const out = { ...next, updatedAt: nowMs };
  const wasComplete = !!(prev && prev.completedAt);
  if (recordState(next) === 'complete' && !wasComplete) out.completedAt = nowMs;
  else if (wasComplete) out.completedAt = prev.completedAt;
  else out.completedAt = null;
  // stored for the projection trigger's cheap gate; always re-derived here
  // so a client can never hand-set a private record public
  out.visibility = deriveVisibility(out);
  return out;
}

export function earnsBeat(next, prev) {
  return recordState(next) === 'complete' && !(prev && prev.completedAt);
}

/** Completion also derives from confirmedAt — the same staleness rule
 *  applies: a tab that never saw the confirmation must not erase it. */
export function guardConfirm(incoming, clientPrev, serverPrev) {
  if (!incoming && !(clientPrev && clientPrev.confirmedAt)
    && serverPrev && serverPrev.confirmedAt) return serverPrev.confirmedAt;
  return incoming || null;
}

export function guardSentence(incoming, clientPrev, serverPrev) {
  const inc = String(incoming || '').trim();
  const knew = String((clientPrev && clientPrev.sentence) || '').trim();
  const server = String((serverPrev && serverPrev.sentence) || '');
  if (!inc && !knew && server.trim()) return server;
  return incoming;
}
